import { useState } from 'react';
import VoicePicker from './VoicePicker';
import { triggerProcess, triggerBlitz, buildTriggerBody } from '../../utils/apiPhaseTriggers';
import { useOnlineOnly } from '../OfflineHint';

/**
 * PhaseTriggerForm — inline form for the owner-only "Process" / "Blitz"
 * triggers introduced in spec 010 US2.
 *
 * The submit handler maps each documented response code (200 / 202 /
 * 403 / 404 / 409 / 412 / 422) to a distinct inline message per FR-006.
 * 422 also re-renders the embedded `<VoicePicker>` via the cached
 * catalogue's subscriber (no extra round-trip).
 *
 * The form exposes the voice picker behind an "Advanced" disclosure
 * (collapsed by default — most users want the system default voice).
 * The PhaseTriggerRequest field `force` is intentionally NOT surfaced in
 * the UI — it remains available only to direct API callers (see spec 010
 * FR-006 / data-model.md "API-only fields"). `tts_model` was removed from
 * the request schema in v0.10.0-rc1; the 202 response still echoes it as
 * a server-resolved read-only value.
 *
 * Props:
 *   - phase: 'process' | 'blitz' — selects which endpoint to call.
 *   - fileId: string — the file the trigger applies to.
 *   - onSubmitted (optional): invoked with the discriminated-union result
 *     so the parent page can refresh its data (progress poll, etc.).
 */
function PhaseTriggerForm({ phase, fileId, onSubmitted }) {
  const offline = useOnlineOnly();
  const [voice, setVoice] = useState('');
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [tone, setTone] = useState('info');

  const reset = () => {
    setVoice('');
    setAdvancedOpen(false);
  };

  const surface = (toneIn, text) => {
    setTone(toneIn);
    setMessage(text);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setMessage(null);
    const body = buildTriggerBody({ voice });
    const dispatcher = phase === 'blitz' ? triggerBlitz : triggerProcess;
    const result = await dispatcher(fileId, body);
    setSubmitting(false);
    switch (result.kind) {
      case 'accepted': {
        const p = result.payload || {};
        surface('success', `Started with voice ${p.voice || 'system default'}${p.tts_model ? ` (model ${p.tts_model})` : ''}.`);
        reset();
        break;
      }
      case 'skipped': {
        const p = result.payload || {};
        surface('info', p.message || 'Already done — nothing to do.');
        break;
      }
      case 'voice_invalid': {
        const p = result.payload || {};
        surface('error', p.error || 'The selected voice is not supported. Pick another.');
        setVoice('');
        break;
      }
      case 'conflict': {
        const p = result.payload || {};
        const id = p.existing_run_id ? ` (run id: ${p.existing_run_id})` : '';
        surface('warn', `A run is already in progress${id}.`);
        break;
      }
      case 'precondition':
        surface('warn', 'Wait for the previous phase to finish before starting this one.');
        break;
      case 'forbidden':
        surface('error', "You don't have permission to trigger this phase.");
        break;
      case 'not_found':
        surface('error', 'File not found.');
        break;
      default:
        surface('error', `Could not start: ${result.status || 'unknown'} error.`);
    }
    if (typeof onSubmitted === 'function') onSubmitted(result);
  };

  const toneClasses = {
    success: 'bg-green-900/30 border-green-700 text-green-100',
    info: 'bg-blue-900/30 border-blue-700 text-blue-100',
    warn: 'bg-yellow-900/30 border-yellow-700 text-yellow-100',
    error: 'bg-red-900/30 border-red-700 text-red-100',
  };

  const isBlitz = phase === 'blitz';
  const heading = isBlitz ? 'Trigger Blitz generation' : 'Trigger audio generation';
  const runText = isBlitz ? 'Generate Blitz' : 'Generate audio';

  return (
    <form onSubmit={handleSubmit} className="border border-gray-700 rounded-lg p-3 bg-gray-800/50" data-testid={`phase-trigger-form-${phase}`}>
      <p className="text-sm font-semibold text-white mb-2">{heading}</p>
      <div className="flex items-center gap-2">
        <button type="submit" disabled={submitting || offline} className="px-3 py-1 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm disabled:opacity-50">
          {submitting ? 'Starting…' : runText}
        </button>
        <button
          type="button"
          onClick={() => setAdvancedOpen((open) => !open)}
          aria-expanded={advancedOpen}
          className="px-2 py-1 text-xs text-gray-300 hover:text-white underline-offset-2 hover:underline"
        >
          {advancedOpen ? 'Hide advanced' : 'Advanced'}
        </button>
      </div>
      {advancedOpen && (
        <label className="block text-xs text-gray-300 mt-2">
          Voice
          <VoicePicker value={voice} onChange={setVoice} disabled={submitting} />
        </label>
      )}
      {message && (
        <p role="status" className={`mt-2 text-xs rounded-md border px-2 py-1 ${toneClasses[tone] || toneClasses.info}`}>{message}</p>
      )}
    </form>
  );
}

export default PhaseTriggerForm;
